import { generateBetItemId } from '../utils/ticketHelpers';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { BetItem } from './BettingSlipDrawer';
import { getPreEvents, getEvent } from '../api/api';
import { EventSnapshot, Market, Outcome } from '../api/types';
import MatchDetailPage from './MatchDetailPage';
import { OddsButton } from './OddsButton';
import { 
  find1x2Market, 
  findRestzeitGeneric, 
  findOUGeneric, 
  findNextGoal, 
  findHandicap, 
  findBTTSGeneric, 
  findDCGeneric, 
  findH2H,
  find1stHalfMarket,
  getMarketAttr,
  isFilterAvailable,
  getMarketOptionsForSport,
  getLayoutGroupForMarketId,
  formatTippLabelWithAttr
} from '../utils/marketParsers';
import { mergeSnapshotWithPatch, applyStreamUpdates } from '../utils/streamMerge';
import { preStreamManager } from '../services/CatalogStreamManager';
import { translateMarketFilterLabel, formatOddsButtonLabel } from '../utils/marketTranslations';

import { SPORT_EMOJI } from './widgets/DesktopLeftSidebar';

const SPORT_ORDER: Record<string, number> = {
  'Soccer': 1,
  'Basketball': 2,
  'Tennis': 3,
  'Ice Hockey': 4,
  'Volleyball': 5,
  'Handball': 6,
  'Table Tennis': 7
};





function countOutcomes(markets: Market[]): number {
  return markets.reduce((acc, m) => acc + m.outcomes.length, 0);
}

const LockIcon = () => (
  <svg className="w-4 h-4 text-gray-400 block" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
  </svg>
);

const JerseyIcon = ({ color, stripeColor }: { color: string; stripeColor?: string }) => (
  <svg className="w-5 h-4 flex-shrink-0" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 3 L8 1 L16 1 L20 3 L23 6 L20 9 L18 8 L18 19 A1 1 0 0 1 17 20 L7 20 A1 1 0 0 1 6 19 L6 8 L4 9 L1 6 Z" fill={color} stroke="#cbd5e1" strokeWidth="1" />
    <path d="M10 1 L12 4 L14 1" stroke="#cbd5e1" strokeWidth="1" />
    {stripeColor && (
      <>
        <line x1="10" y1="5" x2="10" y2="18" stroke={stripeColor} strokeWidth="1.5" />
        <line x1="12" y1="5" x2="12" y2="18" stroke={stripeColor} strokeWidth="1.5" />
        <line x1="14" y1="5" x2="14" y2="18" stroke={stripeColor} strokeWidth="1.5" />
      </>
    )}
  </svg>
);

function getJerseyColors(teamName: string, isHome: boolean): { color: string; stripeColor?: string } {
  const name = teamName.toLowerCase();
  
  if (name.includes('essen') || name.includes('bayern') || name.includes('köln') || name.includes('arsenal') || name.includes('liverpool') || name.includes('manchester utd')) {
    return { color: '#e11d48' }; // red
  }
  if (name.includes('greuther') || name.includes('fürth') || name.includes('real madrid') || name.includes('chelsea') || name.includes('leipzig')) {
    if (isHome) return { color: '#f8fafc', stripeColor: '#64748b' }; // white with gray stripes
  }
  
  let hash = 0;
  for (let i = 0; i < teamName.length; i++) {
    hash = teamName.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const colors = [
    { color: '#e11d48' }, // Red
    { color: '#2563eb' }, // Blue
    { color: '#16a34a' }, // Green
    { color: '#ca8a04' }, // Yellow
    { color: '#f8fafc', stripeColor: '#64748b' }, // White with gray stripes
    { color: '#1e293b' }, // Dark slate
    { color: '#ea580c' }, // Orange
    { color: '#9333ea' }, // Purple
  ];
  
  const index = Math.abs(hash) % colors.length;
  if (!isHome) {
    const awayIndex = (index + 3) % colors.length;
    return colors[awayIndex];
  }
  return colors[index];
}

const StatsIcon = () => (
  <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 transition cursor-pointer" fill="currentColor" viewBox="0 0 20 20">
    <path d="M2 11h2v6H2v-6zm4-4h2v10H6V7zm4-5h2v15h-2V2zm4 8h2v7h-2v-7z" />
  </svg>
);


function formatOdds(odds: string | number): string {
  const parsed = parseFloat(String(odds));
  return isNaN(parsed) ? '-' : parsed.toFixed(2).replace('.', ',');
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

interface TodayMatchesPageProps {
  bets: BetItem[];
  onAddBet: (bet: BetItem) => void;
  onRemoveBet: (id: string) => void;
  onLoginClick?: () => void;
}

const TodayMatchesPage: React.FC<TodayMatchesPageProps> = ({
  bets,
  onAddBet,
  onRemoveBet,
  onLoginClick,
}) => {
  const navigate = useNavigate();
  const { t, locale } = useTranslation();
  const [preEvents, setPreEvents] = useState<EventSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSport, setActiveSport] = useState('Soccer');
  const [activeBetFilter, setActiveBetFilter] = useState<string | number>('Tipp');

  const handleEventClick = (ev: EventSnapshot) => {
    navigate('/match/' + ev.event_id, { state: { event: ev } });
  };

  const loadData = useCallback(async (options?: RequestInit) => {
    try {
      // today's matches = within 24 hours
      const pre = await getPreEvents({ within_hours: 24 }, options);
      setPreEvents(pre.items);
    } catch (e) {
      if ((e as any)?.name !== 'AbortError') {
        console.error('Failed to load today matches', e);
      }
    } finally {
      if (!options?.signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    loadData({ signal: abortController.signal });

    let isPreFirst = true;
    const unsubPre = preStreamManager.subscribe((upserts, deletes) => {
      if (abortController.signal.aborted) return;
      if (isPreFirst) {
        isPreFirst = false;
      } else {
        if (upserts.length > 0 || deletes.length > 0) {
          const applyUpdates = applyStreamUpdates(upserts, deletes, false);
          setPreEvents(prev => applyUpdates(prev) || []);
        }
      }
    });

    return () => {
      abortController.abort();
      unsubPre();
    };
  }, [loadData]);

  // Extract unique sports from loaded events to show as filter pills
  const sports = Array.from(new Set(preEvents.map((e) => e.event?.sport_name).filter(Boolean) as string[]))
    .sort((a, b) => {
      const orderA = SPORT_ORDER[a] ?? 999;
      const orderB = SPORT_ORDER[b] ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      return a.localeCompare(b);
    });
  // ensure activeSport is initialized to a valid sport if 'Soccer' is not loaded, but fallback to Soccer
  useEffect(() => {
    if (sports.length > 0 && !sports.includes(activeSport)) {
      if (sports.includes('Soccer')) {
        setActiveSport('Soccer');
      } else {
        setActiveSport(sports[0]);
      }
    }
  }, [sports, activeSport]);

  const filteredEvents = preEvents.filter(
    (e) => e.event?.sport_name === activeSport
  );

  const isSelected = (eventId: string, marketId: string, outcomeId: string, specifiers?: Record<string, string>) =>
    bets.some((b) => b.id === generateBetItemId(eventId, marketId, outcomeId, specifiers));

  const handleOddsClick = (ev: EventSnapshot, market: Market, outcome: Outcome, tippLabel: string) => {
    const id = generateBetItemId(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers);
    if (isSelected(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers)) {
      onRemoveBet(id);
    } else {

      const formattedTipp = formatTippLabelWithAttr(tippLabel, market);
      onAddBet({
        id,
        matchId: 0,
        matchName: `${ev.event.home.name} - ${ev.event.away.name}`,
        betType: tippLabel === 'Heimsieg' ? 'home' : tippLabel === 'Unentschieden' ? 'draw' : 'away',
        tippLabel: formattedTipp,
        odds: formatOdds(outcome.odds),
        eventId: ev.event_id,
        marketId: market.market_id,
        outcomeId: outcome.outcome_id,
        outcomeName: (outcome as any).player_name || (outcome as any).playerName || outcome.name,
        specifiers: market.specifiers
          ? typeof market.specifiers === 'string'
            ? market.specifiers
            : Object.entries(market.specifiers).map(([k, v]) => `${k}=${v}`).join('|')
          : '',
      });
    }
  };

  const sportFilters = getMarketOptionsForSport(activeSport, false);

  const availableFilters = sportFilters.filter((f) => filteredEvents.some((ev) => isFilterAvailable(f, ev.markets)));

  useEffect(() => {
    const sFilters = getMarketOptionsForSport(activeSport, false);
    const available = sFilters.filter((f) => filteredEvents.some((ev) => isFilterAvailable(f, ev.markets)));
    if (available.length > 0) {
      if (!available.includes(activeBetFilter)) {
        setActiveBetFilter(available[0]);
      }
    } else if (sFilters.length > 0 && !sFilters.includes(activeBetFilter)) {
      setActiveBetFilter(sFilters[0]);
    }
  }, [filteredEvents, activeBetFilter, activeSport]);

  const displayedEvents = filteredEvents.filter((ev) => isFilterAvailable(activeBetFilter, ev.markets)).slice(0, 60);

  // Group events by category name / country
  const groupedEvents = displayedEvents.reduce<Record<string, EventSnapshot[]>>((acc, ev) => {
    const cat = ev.event?.category_name || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(ev);
    return acc;
  }, {});

  // Helper for translating dynamic sport names
  const getSportTitle = (sport: string) => {
    switch (sport) {
      case 'Soccer':
        return t('sport.football');
      case 'Basketball':
        return t('sport.basketball');
      case 'Tennis':
        return t('sport.tennis');
      case 'Ice Hockey':
        return t('sport.iceHockey');
      case 'Handball':
        return t('sport.handball');
      case 'Volleyball':
        return t('sport.volleyball');
      default:
        return sport;
    }
  };

  return (
    <div className="min-h-[500px]">
      {/* Sport tabs */}
      {sports.length > 0 && (
        <div className="bg-[#f5f6f6] border-b border-gray-200 px-4 py-3 flex gap-6 overflow-x-auto mb-3">
          {sports.map((sport) => {
            const active = activeSport === sport;
            const emoji = SPORT_EMOJI[sport] ?? '🏅';
            const count = preEvents.filter((e) => e.event?.sport_name === sport).length;
            return (
              <button
                key={sport}
                onClick={() => setActiveSport(sport)}
                className={`flex flex-col items-center gap-1 min-w-[56px] pb-1 border-b-2 transition ${
                  active ? 'border-red-600 text-gray-900' : 'border-transparent text-gray-500'
                }`}
              >
                <div className="relative">
                  <span className="text-2xl">{emoji}</span>
                  <span className="absolute -top-1 -right-3 text-xs font-bold text-gray-700">{count}</span>
                </div>
                <span className={`text-xs whitespace-nowrap ${active ? 'font-bold' : 'font-medium'}`}>{getSportTitle(sport)}</span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-tipico-red border-t-transparent" />
        </div>
      ) : sports.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center flex flex-col items-center justify-center min-h-[300px]">
          <span className="text-3xl mb-3">📅</span>
          <p className="text-sm font-semibold text-gray-800">{t('misc.noMatches')}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Bet Filter pills */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {availableFilters.map((f) => (
              <button
                key={f}
                onClick={() => setActiveBetFilter(f)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition ${
                  activeBetFilter === f
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-300'
                }`}
              >
                {translateMarketFilterLabel(f, locale)}
              </button>
            ))}
          </div>

          {/* List of tournaments */}
          {displayedEvents.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center text-gray-500 shadow-sm border border-gray-100 flex flex-col items-center justify-center min-h-[250px]">
              <span className="text-3xl mb-3">📅</span>
              <p className="text-sm font-semibold text-gray-800">Bu market için karşılaşma bulunamadı.</p>
            </div>
          ) : (
            Object.entries(groupedEvents).map(([category, events]) => {
              return (
                <div key={category} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 mb-3">
                  <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
                    <span className="text-xs font-bold text-red-600 uppercase tracking-wider">{category}</span>
                  </div>

                  <div className="flex flex-col">
                    {events.map((ev, idx) => {
                      const total = countOutcomes(ev.markets);
                      const matchTime = formatTime(ev.event.scheduled_at);
                      const team1 = ev.event.home.name;
                      const team2 = ev.event.away.name;
                      const homeJersey = getJerseyColors(team1, true);
                      const awayJersey = getJerseyColors(team2, false);
                      const homeScore = ev.status?.home_score;
                      const awayScore = ev.status?.away_score;
                      const hasScores = typeof homeScore === 'number' && typeof awayScore === 'number';

                      let attrMarket: Market | undefined;
                      const layoutGrp = getLayoutGroupForMarketId(activeBetFilter);
                      if (layoutGrp === 'Tipp') { const r = find1x2Market(ev.markets); attrMarket = r?.market; }
                      else if (layoutGrp === 'Tipp Restzeit') { const r = findRestzeitGeneric(ev.markets); attrMarket = r?.market; }
                      else if (layoutGrp === 'Über/Unter') { const r = findOUGeneric(ev.markets, false, ev); attrMarket = r?.market; }
                      else if (layoutGrp === 'Nächstes Tor') { const r = findNextGoal(ev.markets); attrMarket = r?.market; }
                      else if (layoutGrp === 'Handicap' || layoutGrp === 'Satz-Handicap') { const r = findHandicap(ev.markets); attrMarket = r?.market; }
                      else if (layoutGrp === 'Beide Teams treffen') { const r = findBTTSGeneric(ev.markets); attrMarket = r?.market; }
                      else if (layoutGrp === 'Halbzeit-Über/Unter') { const r = findOUGeneric(ev.markets, true, ev); attrMarket = r?.market; }
                      else if (layoutGrp === 'Doppelte Chance') { const r = findDCGeneric(ev.markets); attrMarket = r?.market; }
                      else if (layoutGrp === 'Head-To-Head' || layoutGrp === 'Sieger' || layoutGrp === 'Draw No Bet') { const r = findH2H(ev.markets); attrMarket = r?.market; }
                      else if (layoutGrp === '1. Halbzeit') { const r = find1stHalfMarket(ev.markets); attrMarket = r?.market; }
                      const attr = attrMarket ? getMarketAttr(attrMarket) : null;

                      const renderOddsButtons = (items: { label: string; outcome: Outcome; market: Market; tipp: string }[]) => (
                        <div className="flex gap-2 flex-1">
                          {items.map(({ label, outcome, market, tipp }) => {
                            const sel = isSelected(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers);
                            const oddsVal = formatOdds(outcome.odds);
                            const disabled = !outcome.active || oddsVal === '-' || parseFloat(String(outcome.odds)) <= 1;
                            return (
                              <OddsButton
                                odds={outcome.odds}
                                selected={sel || false}
                                key={label}
                                onClick={() => handleOddsClick(ev, market, outcome, tipp)}
                                disabled={disabled}
                                className={`flex-1 h-[36px] flex items-center justify-between px-2.5 rounded-lg transition text-[13px] font-bold ${
                                  sel
                                    ? 'bg-gray-900 text-white'
                                    : disabled
                                    ? 'bg-gray-50 opacity-40'
                                    : 'bg-[#ebedf0] text-gray-900 hover:bg-[#e2e4e7]'
                                }`}
                              >
                                {disabled ? (
                                  <span className="mx-auto"><LockIcon /></span>
                                ) : (
                                  <>
                                    <span className={`text-[10px] font-bold ${sel ? 'text-white/60' : 'text-gray-400'}`}>
                                      {formatOddsButtonLabel(label, ev.event.home.name, ev.event.away.name, locale)}
                                    </span>
                                    <span className={`text-[13px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>
                                      {oddsVal}
                                    </span>
                                  </>
                                )}
                              </OddsButton>
                            );
                          })}
                        </div>
                      );

                      const renderContent = () => {
                        const layoutGrp = getLayoutGroupForMarketId(activeBetFilter);
                        if (layoutGrp === 'Tipp') {
                          const m = find1x2Market(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons([
                            { label: '1', outcome: m.home, market: m.market, tipp: 'Heimsieg' },
                            { label: 'X', outcome: m.draw, market: m.market, tipp: 'Unentschieden' },
                            { label: '2', outcome: m.away, market: m.market, tipp: 'Auswärtssieg' },
                          ]);
                        }
                        if (layoutGrp === 'Tipp Restzeit') {
                          const m = findRestzeitGeneric(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons([
                            { label: '1', outcome: m.home, market: m.market, tipp: 'Restzeit Heimsieg' },
                            { label: 'X', outcome: m.draw, market: m.market, tipp: 'Restzeit Unentschieden' },
                            { label: '2', outcome: m.away, market: m.market, tipp: 'Restzeit Auswärtssieg' },
                          ]);
                        }
                        if (layoutGrp === 'Über/Unter') {
                          const m = findOUGeneric(ev.markets, false, ev);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons([
                            { label: '+', outcome: m.over, market: m.market, tipp: 'Über 0,5' },
                            { label: '−', outcome: m.under, market: m.market, tipp: 'Unter 0,5' },
                          ]);
                        }
                        if (layoutGrp === 'Nächstes Tor') {
                          const m = findNextGoal(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons([
                            { label: '1', outcome: m.home, market: m.market, tipp: 'Nächstes Tor Heim' },
                            { label: 'X', outcome: m.none, market: m.market, tipp: 'Kein Tor' },
                            { label: '2', outcome: m.away, market: m.market, tipp: 'Nächstes Tor Auswärts' },
                          ]);
                        }
                        if (layoutGrp === 'Handicap' || layoutGrp === 'Satz-Handicap') {
                          const m = findHandicap(ev.markets);
                          if (!m) {
                            const slots = layoutGrp === 'Satz-Handicap' ? [1, 2] : [1, 2, 3];
                            return <div className="flex gap-2 flex-1">{slots.map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          }
                          const items: { label: string; outcome: Outcome; market: Market; tipp: string }[] = [
                            { label: '1', outcome: m.o1, market: m.market, tipp: layoutGrp === 'Satz-Handicap' ? 'Satz-Handicap 1' : 'Handicap 1' },
                          ];
                          if (m.o3) items.push({ label: 'X', outcome: m.o3, market: m.market, tipp: 'Handicap X' });
                          items.push({ label: '2', outcome: m.o2, market: m.market, tipp: layoutGrp === 'Satz-Handicap' ? 'Satz-Handicap 2' : 'Handicap 2' });
                          return renderOddsButtons(items);
                        }
                        if (layoutGrp === 'Beide Teams treffen') {
                          const m = findBTTSGeneric(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons([
                            { label: 'Ja', outcome: m.yes, market: m.market, tipp: 'Ja' },
                            { label: 'Nein', outcome: m.no, market: m.market, tipp: 'Nein' },
                          ]);
                        }
                        if (layoutGrp === 'Halbzeit-Über/Unter') {
                          const m = findOUGeneric(ev.markets, true, ev);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons([
                            { label: '+', outcome: m.over, market: m.market, tipp: '1.Hz Über 0,5' },
                            { label: '−', outcome: m.under, market: m.market, tipp: '1.Hz Unter 0,5' },
                          ]);
                        }
                        if (layoutGrp === 'Doppelte Chance') {
                          const m = findDCGeneric(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons([
                            { label: '1X', outcome: m.o1x, market: m.market, tipp: '1X' },
                            { label: '12', outcome: m.o12, market: m.market, tipp: '12' },
                            { label: 'X2', outcome: m.ox2, market: m.market, tipp: 'X2' },
                          ]);
                        }
                        if (layoutGrp === 'Head-To-Head' || layoutGrp === 'Sieger' || layoutGrp === 'Draw No Bet') {
                          const m = findH2H(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          const tippLabelHome = layoutGrp === 'Sieger' ? 'Sieger Heim' : layoutGrp === 'Draw No Bet' ? 'Draw No Bet Heim' : 'H2H Heim';
                          const tippLabelAway = layoutGrp === 'Sieger' ? 'Sieger Auswärts' : layoutGrp === 'Draw No Bet' ? 'Draw No Bet Auswärts' : 'H2H Auswärts';
                          return renderOddsButtons([
                            { label: '1', outcome: m.home, market: m.market, tipp: tippLabelHome },
                            { label: '2', outcome: m.away, market: m.market, tipp: tippLabelAway },
                          ]);
                        }
                        if (layoutGrp === '1. Halbzeit') {
                          const m = find1stHalfMarket(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons([
                            { label: '1', outcome: m.home, market: m.market, tipp: '1.Hz Heimsieg' },
                            { label: 'X', outcome: m.draw, market: m.market, tipp: '1.Hz Unentschieden' },
                            { label: '2', outcome: m.away, market: m.market, tipp: '1.Hz Auswärtssieg' },
                          ]);
                        }
                        return null;
                      };

                      return (
                        <div
                          key={ev.event_id}
                          className={`p-4 ${
                            idx > 0 ? 'border-t border-gray-100/70' : ''
                          } hover:bg-gray-50/50 transition`}
                        >
                          {/* Top row: Time, Teams, Scores, Stats */}
                          <div className="flex items-center justify-between mb-3.5">
                            <div
                              className="flex items-center gap-3.5 flex-1 min-w-0 cursor-pointer"
                              onClick={() => handleEventClick(ev)}
                            >
                              <span className="text-[13px] font-bold text-gray-800 min-w-[38px] select-none">
                                {matchTime}
                              </span>
                              <div className="flex-1 min-w-0 flex flex-col gap-2">
                                {/* Home Team */}
                                <div className="flex items-center justify-between w-full">
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <img src={ev.event.home.crest_url || '/default-crest.svg'} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).src = '/default-crest.svg'; }} />
                                    <span className="text-[14px] font-bold text-gray-900 truncate leading-tight">
                                      {team1}
                                    </span>
                                  </div>
                                  {hasScores && (
                                    <span className="text-[13px] font-bold text-gray-500 mr-2 flex-shrink-0">
                                      ({homeScore})
                                    </span>
                                  )}
                                </div>
                                {/* Away Team */}
                                <div className="flex items-center justify-between w-full">
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <img src={ev.event.away.crest_url || '/default-crest.svg'} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).src = '/default-crest.svg'; }} />
                                    <span className="text-[14px] font-bold text-gray-900 truncate leading-tight">
                                      {team2}
                                    </span>
                                  </div>
                                  {hasScores && (
                                    <span className="text-[13px] font-bold text-gray-500 mr-2 flex-shrink-0">
                                      ({awayScore})
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex-shrink-0 ml-3 mr-1" onClick={() => handleEventClick(ev)}>
                              <StatsIcon />
                            </div>
                          </div>

                          {/* Bottom row: Attributes, Odds buttons, More markets */}
                          <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-gray-100/70">
                            <div className="flex-1 flex items-center gap-2 max-w-[280px]">
                              {attr && (
                                <span className="text-[10px] font-bold text-gray-900 bg-gray-100 px-1.5 py-1 rounded flex-shrink-0 select-none">
                                  {attr}
                                </span>
                              )}
                              {renderContent()}
                            </div>
                            <button
                              className="text-[11px] font-bold text-gray-400 flex items-center justify-center gap-0.5 hover:text-gray-600 pl-2 pr-1 h-9 flex-shrink-0"
                              onClick={() => handleEventClick(ev)}
                            >
                              <span>{total}</span>
                              <span className="text-[14px] leading-none text-gray-400 font-normal">›</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default TodayMatchesPage;
